import type { ApolloClient } from '@apollo/client';
import {
  MessagesDocument,
  MatterConversationsDocument,
} from '~/graphql/queries/conversation';
import { MatterActivitiesDocument } from '~/graphql/queries/matterActivityQueries';
import { ME_QUERY } from '~/graphql/queries/me';
import { DEFAULT_MESSAGE_LIMIT } from '~/shared/constants';
import {
  MatterActivityEventType,
  type UserEventsSubscription,
} from '~/gql/graphql';

// Activity event types that imply the matter's retainer list is now stale.
// EXPERT_HIRED is intentionally excluded: it's recorded alongside RETAINER_REQUESTED
// in the same publish batch (see Retainer.service.createRetainer), so including it
// would double-evict and risk a duplicate refetch. Other EXPERT_HIRED paths fire
// when no retainers exist yet, so they don't affect the retainer list.
const RETAINER_AFFECTING_EVENTS: ReadonlySet<MatterActivityEventType> = new Set(
  [
    MatterActivityEventType.RetainerRequested,
    MatterActivityEventType.RetainerApproved,
    MatterActivityEventType.RetainerDeclined,
    MatterActivityEventType.RetainerClosed,
    MatterActivityEventType.RetainerWorkCompleted,
  ],
);

type MessageReceivedEvent = Extract<
  UserEventsSubscription['userEvents'],
  { __typename?: 'MessageReceived' }
>;

type MatterActivityReceivedEvent = Extract<
  UserEventsSubscription['userEvents'],
  { __typename?: 'MatterActivityReceived' }
>;

export function handleMessageReceived(
  { message }: MessageReceivedEvent,
  client: ApolloClient<object>,
) {
  client.cache.updateQuery(
    {
      query: MessagesDocument,
      variables: {
        conversationId: message.conversationId,
        limit: DEFAULT_MESSAGE_LIMIT,
      },
    },
    (existing) => {
      if (!existing) {
        return existing;
      }

      const withoutOptimistic = existing.messages.filter(
        (existingMessage) =>
          !String(existingMessage.id).startsWith('optimistic-'),
      );

      const alreadyExists = withoutOptimistic.some(
        (existingMessage) => existingMessage.id === message.id,
      );

      if (alreadyExists) {
        return { ...existing, messages: withoutOptimistic };
      }

      return {
        ...existing,
        messages: [message, ...withoutOptimistic],
      };
    },
  );

  const meData = client.cache.readQuery({ query: ME_QUERY });
  const isSentByCurrentUser = meData?.me?.email === message.sender.email;

  if (isSentByCurrentUser) {
    return;
  }

  const conversationRef = client.cache.identify({
    __typename: 'Conversation',
    id: message.conversationId,
  });
  const cacheData = client.cache.extract() as Record<
    string,
    { matterId?: string } | undefined
  >;
  const matterId = conversationRef
    ? cacheData[conversationRef]?.matterId
    : undefined;

  if (!matterId) {
    return;
  }

  client.cache.updateQuery(
    {
      query: MatterConversationsDocument,
      variables: { matterId },
    },
    (existing) => {
      if (!existing) {
        return existing;
      }

      return {
        ...existing,
        matterConversations: existing.matterConversations.map((conversation) =>
          conversation.id === message.conversationId
            ? {
                ...conversation,
                unreadCount: conversation.unreadCount + 1,
              }
            : conversation,
        ),
      };
    },
  );
}

export function handleMatterActivityReceived(
  { event }: MatterActivityReceivedEvent,
  client: ApolloClient<object>,
) {
  client.cache.updateQuery(
    {
      query: MatterActivitiesDocument,
      variables: { matterId: event.matterId },
    },
    (existing) => {
      if (!existing) {
        return existing;
      }

      const alreadyExists = existing.matterActivities.some(
        (existingEvent) => existingEvent.id === event.id,
      );

      if (alreadyExists) {
        return existing;
      }

      return {
        ...existing,
        matterActivities: [event, ...existing.matterActivities],
      };
    },
  );

  // Retainer state changes are surfaced as matter activities. Evict the
  // matter's `retainersForMatter` field so any mounted `useRetainersForMatter`
  // refetches with fresh data; if no component is subscribed, this is a no-op.
  if (RETAINER_AFFECTING_EVENTS.has(event.eventType)) {
    client.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'retainersForMatter',
      args: { matterId: event.matterId },
    });
    client.cache.gc();
  }
}
